/**
 * NeoCore · Datos SAT — NIT + fecha de nacimiento por asociado
 *
 * Herramienta para Nataly:
 *   1. Consultar el NIT de cada asociado en el portal SAT (requiere DPI +
 *      fecha de nacimiento) y guardarlo acá.
 *   2. Corregir fechas de nacimiento mal leídas por el OCR del DPI
 *      (bug ±1 día detectado ago-2026). Cada corrección deja audit_log.
 *
 * Regla NIT: legacy si existe · si está vacío, el NIT efectivo es el CUI
 * (armonización SAT marzo 2025). Guardar solo NITs legacy distintos al CUI.
 *
 * RPCs: listar_datos_sat / actualizar_nit_asociado / corregir_fecha_nacimiento
 * Migración 0149.
 */

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { MagnifyingGlass, ArrowSquareOut, Check, PencilSimple, X } from '@phosphor-icons/react'

type Row = {
  asociado_id: string
  no_asociado: string
  nombre_completo: string
  dpi: string | null
  nit: string | null
  nit_es_cui: boolean
  fecha_nacimiento: string | null
  feis_id: string | null
  dpi_frente_path: string | null
}

const SAT_URL = 'https://portal.sat.gob.gt/portal/consulta-cui-nit/'

export function NeoCoreDatosSatPage() {
  const [rows, setRows] = useState<Row[]>([])
  const [search, setSearch] = useState('')
  const [soloSinNit, setSoloSinNit] = useState(false)
  const [loading, setLoading] = useState(false)
  const [editando, setEditando] = useState<string | null>(null)   // asociado_id en edición
  const [nitDraft, setNitDraft] = useState('')
  const [fechaDraft, setFechaDraft] = useState('')
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null)

  const cargar = async () => {
    setLoading(true)
    const { data, error } = await supabase.rpc('listar_datos_sat', {
      p_search: search || null,
      p_solo_sin_nit: soloSinNit,
    })
    setLoading(false)
    if (error) { setMsg({ tipo: 'error', texto: error.message }); return }
    setRows((data ?? []) as Row[])
  }

  useEffect(() => { cargar() }, [soloSinNit])

  const [fotoUrl, setFotoUrl] = useState<string | null>(null)

  const empezarEdicion = async (r: Row) => {
    setEditando(r.asociado_id)
    setNitDraft(r.nit ?? '')
    setFechaDraft(r.fecha_nacimiento ?? '')
    setMsg(null)
    setFotoUrl(null)
    // Foto del DPI para verificar la fecha contra el documento
    if (r.dpi_frente_path) {
      const { data } = await supabase.storage.from('kyc')
        .createSignedUrl(r.dpi_frente_path, 300)
      if (data?.signedUrl) setFotoUrl(data.signedUrl)
    }
  }

  const guardar = async (r: Row) => {
    setMsg(null)
    // NIT: solo si cambió
    if ((r.nit ?? '') !== nitDraft.trim()) {
      const { error } = await supabase.rpc('actualizar_nit_asociado', {
        p_asociado_id: r.asociado_id,
        p_nit: nitDraft.trim(),
      })
      if (error) { setMsg({ tipo: 'error', texto: `NIT: ${error.message}` }); return }
    }
    // Fecha: solo si cambió y hay valor
    if (fechaDraft && r.fecha_nacimiento !== fechaDraft) {
      const { error } = await supabase.rpc('corregir_fecha_nacimiento', {
        p_asociado_id: r.asociado_id,
        p_fecha: fechaDraft,
        p_motivo: 'Corrección lectura OCR del DPI · verificado contra documento físico',
      })
      if (error) { setMsg({ tipo: 'error', texto: `Fecha: ${error.message}` }); return }
    }
    setEditando(null)
    setMsg({ tipo: 'ok', texto: `${r.nombre_completo} actualizado` })
    await cargar()
  }

  const sinNit = rows.filter(r => r.nit_es_cui).length

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 500 }}>Datos SAT · NIT y fecha de nacimiento</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--muted-foreground)', fontSize: 13, maxWidth: 720 }}>
          Consultá el NIT en el portal SAT con el DPI + fecha de nacimiento y guardalo acá.
          Si la fecha no coincide con el DPI físico (el OCR a veces lee ±1 día), corregila
          en la misma fila — queda registrado quién y cuándo.
        </p>
      </div>

      {/* Toolbar */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 12, padding: 12, alignItems: 'center', flexWrap: 'wrap',
        background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8,
      }}>
        <MagnifyingGlass size={16} color="var(--muted-foreground)" />
        <input
          placeholder="Buscar por nombre, código o DPI…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && cargar()}
          style={{ ...inp, flex: 1, minWidth: 220 }}
        />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--muted-foreground)' }}>
          <input type="checkbox" checked={soloSinNit} onChange={e => setSoloSinNit(e.target.checked)} />
          Solo sin NIT legacy
        </label>
        <button onClick={cargar} disabled={loading} style={btnPrimary}>
          {loading ? 'Cargando…' : 'Buscar'}
        </button>
        <a href={SAT_URL} target="_blank" rel="noopener noreferrer" style={{ ...btnGhost, textDecoration: 'none' }}>
          <ArrowSquareOut size={13} /> Abrir consulta SAT
        </a>
      </div>

      {msg && (
        <div style={{
          padding: 10, borderRadius: 6, fontSize: 13, marginBottom: 12,
          background: msg.tipo === 'ok' ? '#dcfce7' : '#fee2e2',
          color: msg.tipo === 'ok' ? '#166534' : '#991b1b',
        }}>
          {msg.texto}
        </div>
      )}

      <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--muted-foreground)' }}>
        {rows.length} asociados · {sinNit} sin NIT legacy (usan CUI como NIT por armonización SAT 2025)
      </div>

      {/* Tabla */}
      <div style={{ background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: 'var(--n-50, #f4f4f5)', borderBottom: '1px solid var(--border)' }}>
            <tr>
              <th style={th}>Código</th>
              <th style={th}>Asociado</th>
              <th style={th}>DPI (CUI)</th>
              <th style={th}>Fecha nacimiento</th>
              <th style={th}>NIT</th>
              <th style={th}>Estado</th>
              <th style={{ ...th, width: 110 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--muted-foreground)' }}>
                Sin resultados
              </td></tr>
            )}
            {rows.map(r => {
              const enEdicion = editando === r.asociado_id
              return (
                <>
                <tr key={r.asociado_id} style={{
                  borderBottom: enEdicion ? 'none' : '1px solid var(--border)',
                  background: enEdicion ? 'var(--brand-50, #fff5ed)' : undefined,
                }}>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.no_asociado}</td>
                  <td style={{ ...td, fontWeight: 500 }}>{r.nombre_completo}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.dpi ?? '—'}</td>
                  <td style={td}>
                    {enEdicion ? (
                      <input type="date" value={fechaDraft} onChange={e => setFechaDraft(e.target.value)}
                        style={{ ...inp, width: 140 }} />
                    ) : (
                      r.fecha_nacimiento ?? <span style={{ color: 'var(--muted-foreground)' }}>—</span>
                    )}
                  </td>
                  <td style={td}>
                    {enEdicion ? (
                      <input value={nitDraft} onChange={e => setNitDraft(e.target.value)}
                        placeholder="NIT legacy (vacío = usa CUI)"
                        style={{ ...inp, width: 160, fontFamily: 'monospace' }} />
                    ) : (
                      <span style={{ fontFamily: 'monospace', fontSize: 11 }}>
                        {r.nit ?? <span style={{ color: 'var(--muted-foreground)' }}>{r.dpi} *</span>}
                      </span>
                    )}
                  </td>
                  <td style={td}>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                      fontSize: 10, fontWeight: 500,
                      background: r.nit_es_cui ? '#dbeafe' : '#dcfce7',
                      color: r.nit_es_cui ? '#1e40af' : '#166534',
                    }}>
                      {r.nit_es_cui ? 'NIT = CUI' : 'NIT legacy'}
                    </span>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {enEdicion ? (
                      <>
                        <button onClick={() => guardar(r)} style={{ ...btnMini, color: '#047857' }} title="Guardar">
                          <Check size={14} weight="bold" />
                        </button>
                        <button onClick={() => setEditando(null)} style={{ ...btnMini, color: 'var(--muted-foreground)' }} title="Cancelar">
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <button onClick={() => empezarEdicion(r)} style={btnMini} title="Editar NIT / fecha">
                        <PencilSimple size={14} />
                      </button>
                    )}
                  </td>
                </tr>
                {/* Fila expandida: foto del DPI para verificar la fecha */}
                {enEdicion && (
                  <tr key={`${r.asociado_id}-foto`} style={{ borderBottom: '1px solid var(--border)', background: 'var(--brand-50, #fff5ed)' }}>
                    <td colSpan={7} style={{ padding: '4px 12px 14px' }}>
                      {fotoUrl ? (
                        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                          <img src={fotoUrl} alt={`DPI de ${r.nombre_completo}`}
                            style={{ maxWidth: 420, width: '100%', borderRadius: 8, border: '1px solid var(--border)' }} />
                          <p style={{ fontSize: 12, color: 'var(--muted-foreground)', maxWidth: 280, lineHeight: 1.5 }}>
                            Compará la <b>fecha de nacimiento del documento</b> con el campo de la fila.
                            El OCR a veces intercambia día/mes o corre un día — el DPI físico manda.
                          </p>
                        </div>
                      ) : r.dpi_frente_path ? (
                        <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>Cargando foto del DPI…</span>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>Este asociado no tiene foto de DPI en el sistema.</span>
                      )}
                    </td>
                  </tr>
                )}
                </>
              )
            })}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: 10, fontSize: 11, color: 'var(--muted-foreground)' }}>
        * Sin NIT legacy: el NIT efectivo es el CUI (armonización SAT marzo 2025).
        Solo guardá NIT cuando el portal SAT devuelva uno distinto al CUI.
      </p>
    </div>
  )
}

const inp: React.CSSProperties = {
  padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6,
  fontSize: 12, background: 'var(--surface, #fff)', fontFamily: 'inherit', boxSizing: 'border-box',
}
const btnPrimary: React.CSSProperties = {
  padding: '8px 16px', border: 'none', borderRadius: 6,
  background: 'var(--brand-600, #FF5B04)', color: '#fff',
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
}
const btnGhost: React.CSSProperties = {
  padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6,
  background: 'var(--surface, #fff)', fontSize: 12, cursor: 'pointer', color: 'var(--ink)',
  display: 'inline-flex', alignItems: 'center', gap: 4,
}
const btnMini: React.CSSProperties = {
  padding: 5, border: 'none', background: 'transparent', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center',
}
const th: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'left', fontSize: 11,
  color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500,
}
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' }
